/**
 * ChalkText — display headline component.
 *
 * Fraunces with small caps, generous tracking. Three sizes:
 *   - display     64px   page-level heroes
 *   - heading     32px   section heads
 *   - subheading  20px   sub-section labels
 *
 * Optional `underline` renders a hand-drawn pencil underline below the
 * text via inline SVG with the chalkRoughen filter applied.
 */

import { type CSSProperties, type ReactNode } from 'react';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { FILTER_IDS } from '../../filters';

type ChalkTextSize = 'display' | 'heading' | 'subheading';
type ChalkTextElement = 'h1' | 'h2' | 'h3' | 'p' | 'span';

interface ChalkTextProps {
  children: ReactNode;
  size?: ChalkTextSize;
  as?: ChalkTextElement;
  underline?: boolean;
  color?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  style?: CSSProperties;
}

function sizeStyles(size: ChalkTextSize): CSSProperties {
  switch (size) {
    case 'display':
      return { fontSize: 64, lineHeight: 1.05, letterSpacing: '0.04em' };
    case 'heading':
      return { fontSize: 32, lineHeight: 1.15, letterSpacing: '0.04em' };
    case 'subheading':
    default:
      return { fontSize: 20, lineHeight: 1.25, letterSpacing: '0.04em' };
  }
}

export function ChalkText({
  children,
  size = 'heading',
  as,
  underline = false,
  color = surfaces.chalk,
  align,
  className,
  style,
}: ChalkTextProps) {
  const Tag = (as ?? defaultTagFor(size)) as keyof JSX.IntrinsicElements;
  const sized = sizeStyles(size);

  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        textAlign: align,
        ...style,
      }}
    >
      <Tag
        style={{
          fontFamily: fontStacks.display,
          fontWeight: 600,
          color,
          // Fraunces SOFT axis dialled to 50 for warmth + small caps for
          // the "academic-but-handcrafted" Pentiment vibe.
          fontVariationSettings: '"opsz" 96, "SOFT" 50',
          fontVariantCaps: 'all-small-caps',
          margin: 0,
          ...sized,
        }}
      >
        {children}
      </Tag>
      {underline && <ChalkUnderline color={color} />}
    </span>
  );
}

function defaultTagFor(size: ChalkTextSize): ChalkTextElement {
  if (size === 'display') return 'h1';
  if (size === 'heading') return 'h2';
  return 'h3';
}

interface ChalkUnderlineProps {
  color: string;
}

/** A hand-drawn pencil underline rendered as a thin path. The chalk
 *  roughen filter pushes pixels around so the line never reads as
 *  vector-perfect. */
function ChalkUnderline({ color }: ChalkUnderlineProps) {
  return (
    <svg
      aria-hidden
      focusable="false"
      width="100%"
      height="14"
      viewBox="0 0 240 14"
      preserveAspectRatio="none"
      style={{
        display: 'block',
        marginTop: 4,
        filter: `url(#${FILTER_IDS.chalkRoughen})`,
      }}
    >
      <path
        d="M2 8 Q 60 4, 120 7 T 238 6"
        fill="none"
        stroke={color}
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.78"
      />
    </svg>
  );
}
